import React from "react";
import { useProductConfiguratorStore } from "components/_ProductConfigurator/store/ProductConfiguratorStore";
import { useTranslate } from "hooks";
import cx from "classnames";
import { ProductResultTabs } from "components/_ProductConfigurator/SubComponents/ProductConfigurationResult/ProductResultMainView/ProductResultTabs/ProductResultTabs";
import { TechnicalDesignInputs } from "components/_ProductConfigurator/SubComponents/ProductConfigurationResult/TechnicalDesignInputs";
import { TechnicalDesignResults } from "components/_ProductConfigurator/SubComponents/ProductConfigurationResult/TechnicalDesignResults";
import { TechnicalDesignMedia } from "components/_ProductConfigurator/SubComponents/ProductConfigurationResult/TechnicalDesignMedia";
import { TableCard } from "components/InputCards/TableCard";
import {
  getBodyData,
  getHeadData,
} from "components/_ProductConfigurator/_helpers";
import { TabContent, TabPane } from "reactstrap";
import { productConfiguratorTabsEnum } from "variables/enums";
import { ProductConfiguratorResultSection } from "components/_ProductConfigurator/SubComponents/ProductConfigurationResult/ProductResultMainView/ProductConfiguratoResultSection";
import { ProductSuggestionResultView } from "components/_ProductConfigurator/SubComponents/ProductConfigurationResult/ProductResultMainView/ProductSuggestionResultView/ProductSuggestionResultView";

import { ResultingListFormContextProvider } from "components/_ProductConfigurator/SubComponents/ProductConfigurationResult/ProductResultMainView/ProductSuggestionResultView/providers/ResultingListFormContextProvider";

import styles from "./ProductConfigurationResultMainView.module.scss";
import { useAuthStore } from "stores/authStore";
import { FeatureFlagKey } from "utils/featureFlags/resolveFlags/featureFlags.type";

export const ProductConfiguratorResultMainView = ({
  strategyOptions,
  selectedOption,
  handleSelectStrategy,
}) => {
  const { product, productImageList } =
    useProductConfiguratorStore.use.productData() || {};
  const requestParams = useProductConfiguratorStore.use.requestParams();
  const activeTab = useProductConfiguratorStore.use.activeTab();
  const featureFlags = useAuthStore.use.featureFlags();

  const translate = useTranslate();

  const isProductAccessory = !!product?.accessoryInfo?.accessoryId;

  const isResultingListEnabled =
    featureFlags?.[FeatureFlagKey.PRODUCT_RESULTING_LIST];

  return (
    <div className="d-flex flex-column h-100">
      <div
        className={cx(styles.gridContainer, "flex-grow-1 min-h-0", {
          [styles.withoutTabs]:
            !product?.listViewConfiguration?.targetProperties,
        })}
      >
        {isResultingListEnabled &&
          product?.listViewConfiguration?.targetProperties && (
            <div className={styles.tabsCell}>
              <ProductResultTabs />
            </div>
          )}

        <aside
          className={cx(
            styles.mediaCell,
            "min-h-0 d-flex flex-column bg-white ms-0 mb-xxl-0 ms-xxl-3",
          )}
        >
          <ProductConfiguratorResultSection
            title={translate("PRODUCT_CONFIGURATOR_MEDIA")}
            classNames="d-flex flex-column h-100"
            shouldHaveMaximizeBtn={false}
          >
            <TechnicalDesignMedia
              images={productImageList?.productImages}
              requestParams={requestParams}
            />
          </ProductConfiguratorResultSection>
        </aside>

        <div className={cx(styles.mainContent, "bg-white pb-3")}>
          <TabContent
            activeTab={
              isResultingListEnabled
                ? activeTab
                : productConfiguratorTabsEnum.singleSelection
            }
            className="h-100 overflow-auto"
          >
            <TabPane tabId={productConfiguratorTabsEnum.singleSelection}>
              <div className={styles.mainGrid}>
                <ProductConfiguratorResultSection
                  title={translate("PRODUCT_CONFIGURATOR_INPUT")}
                  classNames={cx(
                    styles.inputsCell,
                    "d-flex flex-column border rounded" +
                      " p-1 m-2" +
                      " p-xxl-2 m-xxl-3" +
                      " m-xxxl-4 me-xxxl-3 mb-xxxl-3",
                  )}
                >
                  <TechnicalDesignInputs
                    selectedOption={selectedOption}
                    inputs={product?.inputs}
                    productConfiguration={product}
                    handleSelectStrategy={handleSelectStrategy}
                    strategyOptions={strategyOptions}
                    isDisabled={isProductAccessory}
                  />
                </ProductConfiguratorResultSection>

                <ProductConfiguratorResultSection
                  title={translate("PRODUCT_CONFIGURATOR_RESULTS")}
                  classNames={cx(
                    styles.resultsCell,
                    "d-flex flex-column border rounded" +
                      " p-1 m-2" +
                      " p-xxl-2 m-xxl-3 ms-xxl-0" +
                      " m-xxxl-4 ms-xxxl-2 mb-xxxl-3 ",
                  )}
                >
                  <TechnicalDesignResults
                    productConfiguration={product}
                    hasInputsAndNotModal
                    requestParams={requestParams}
                  />
                </ProductConfiguratorResultSection>
                {product?.outputsAcoustic?.length > 0 && (
                  <ProductConfiguratorResultSection
                    title={translate(
                      "PRODUCT_CONFIGURATOR_REPORT_ACOUSTING_LABEL",
                    )}
                    classNames={cx(
                      styles.acousticCell,
                      "border rounded p-1 m-2" +
                        " m-xxl-3 mt-xxl-0 p-xxl-2" +
                        " m-xxxl-4 mt-xxxl-2 p-xxxl-3",
                    )}
                    viewWithTable={true}
                  >
                    <div className="overflow-auto w-100 mt-3">
                      <TableCard
                        head={getHeadData(product?.outputsAcoustic)}
                        body={getBodyData(product?.outputsAcoustic)}
                        classNames="w-100"
                        headerTitle={
                          <th>
                            {translate(
                              "PRODUCT_CONFIGURATOR_REPORT_ACOUSTING_LABEL",
                            )}
                          </th>
                        }
                      />
                    </div>
                  </ProductConfiguratorResultSection>
                )}
              </div>
            </TabPane>

            <TabPane tabId={productConfiguratorTabsEnum.productSuggestion}>
              <div className={cx(styles.productSuggestion, "h-100")}>
                <ResultingListFormContextProvider>
                  <ProductSuggestionResultView
                    {...{
                      strategyOptions,
                      handleSelectStrategy,
                      selectedOption,
                    }}
                  />
                </ResultingListFormContextProvider>
              </div>
            </TabPane>
          </TabContent>
        </div>
      </div>
    </div>
  );
};
